<script lang="ts" setup>
// Import styles.
import "vidstack/player/styles/base.css";
// Register elements.
import "vidstack/player";
import "vidstack/player/ui";
import "vidstack/icons";

import {
  isHLSProvider,
  type MediaCanPlayEvent,
  type MediaProviderChangeEvent,
} from "vidstack";
import type { MediaPlayerElement } from "vidstack/elements";
import { computed, onMounted, ref, shallowRef } from "vue";

import VideoLayout from "./components/layouts/VideoLayout.vue";
import MasterLayer from "@/front/components/MasterLayer.vue";
import { cdnImageUrl } from '@/front/composables/ImageComposer.js'

import { throttle } from 'lodash'
import { MessageComposer } from '@/front/composables/MessageComposer.js';
const { emitMessage } = MessageComposer()
import { convertDecitimeToKey } from '@/front/composables/TimeFormatter.js'

const ContainerDebug = defineAsyncComponent(
  () => import("@/front/components/ContainerDebug.vue")
);
const IframeDebugger = defineAsyncComponent(
  () => import("@/front/components/IframeDebugger.vue")
);

import { preloadManager } from '@/front/services/PreloadManager.js'


// const MainThreadMonitor = import('@/front/performance/MainThreadMonitor.js')
const threadMonitor = ref(null)


import { VideoStore } from "@/front/stores/video_store.js";
import { UploadStore } from "@/front/stores/upload_store.js";
import { PlayerStore } from "@/front/stores/player_store.js";
import { AnnStore } from "@/front/stores/ann_store.js";
import { PlaybackStore } from "@/front/stores/playback_store.js";
import { ClickStore } from "@/front/stores/click_store.js";
import { ClipStore } from "@/front/stores/clip_store.js";
import { BrowserHelper } from "@/front/composables/BrowserHelper";
const browserInfo = BrowserHelper();

const { t } = useI18n();

const clickStore = ClickStore();
const videoStore = VideoStore();
const playerStore = PlayerStore();
const uploadStore = UploadStore();
const annStore = AnnStore();

const clipStore = ClipStore();
import { StyleService } from "@/front/services/StyleService.js";
import { useRoute } from "vue-router";
const route = useRoute();
import { useGlobalRenderTracker } from '@/front/composables/GlobalRenderTracker.js'
if (playerStore.isDebugPerformance) {
  useGlobalRenderTracker('Player', { logLevel: 'none' })
}

const props = defineProps({
  isEnd: {
    type: Boolean,
    default: false,
  },
  isMenu: {
    type: Boolean,
    default: false,
  },
  isPlayback: {
    type: Boolean,
    default: false,
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  time: {
    type: Number,
    default: null,
  },
  isEditor: {
    type: Boolean,
    default: false,
  },
  quickStartHighlightTarget: {
    type: String,
    default: "",
  },
});

const $player = ref<MediaPlayerElement>();
const $src = shallowRef("");
let ratioUpdateFrame: number | null = null;
let disposeHlsInstanceListener: (() => void) | null = null;
const IOS_OVERFLOW_FIX_CLASS = "ios-overflow-scroll-fix";
const EMBED_WHEEL_PASS_CLASS = "embed-wheel-pass-through";
let embedWheelTarget: HTMLElement | null = null;

function applyIOSOverflowFix() {
  if (!browserInfo.isIOS || !playerStore.isWebView) {
    return;
  }
  document.documentElement.classList.add(IOS_OVERFLOW_FIX_CLASS);
  document.body?.classList.add(IOS_OVERFLOW_FIX_CLASS);
  log.info(2, "🌜 Player applied iOS overflow workaround class");
}

function removeIOSOverflowFix() {
  document.documentElement.classList.remove(IOS_OVERFLOW_FIX_CLASS);
  document.body?.classList.remove(IOS_OVERFLOW_FIX_CLASS);
}

function applyEmbedWheelPassThroughFix() {
  const isEmbed = window !== window.parent;
  if (!isEmbed) {
    return;
  }
  embedWheelTarget = document.getElementById("player-wrapper");
  if (!embedWheelTarget) {
    return;
  }
  // Allow page-level wheel scrolling while cursor is over the embed by preventing
  // internal listeners from swallowing wheel events.
  embedWheelTarget.addEventListener("wheel", handleEmbedWheelCapture, {
    capture: true,
    passive: true,
  });
  document.documentElement.classList.add(EMBED_WHEEL_PASS_CLASS);
  document.body?.classList.add(EMBED_WHEEL_PASS_CLASS);
}

function removeEmbedWheelPassThroughFix() {
  if (embedWheelTarget) {
    embedWheelTarget.removeEventListener("wheel", handleEmbedWheelCapture, true);
    embedWheelTarget = null;
  }
  document.documentElement.classList.remove(EMBED_WHEEL_PASS_CLASS);
  document.body?.classList.remove(EMBED_WHEEL_PASS_CLASS);
}

function handleEmbedWheelCapture(event: WheelEvent) {
  // Keep internal scrollable dialogs/layers functional.
  if (clickStore.hasActiveDialog || annStore.pausedInteraction) {
    return;
  }
  event.stopImmediatePropagation();
}

function scheduleRatioUpdate() {
  if (ratioUpdateFrame !== null) {
    return;
  }
  ratioUpdateFrame = window.requestAnimationFrame(() => {
    ratioUpdateFrame = null;
    playerStore.updateRatios();
  });
}

function handleViewportGeometryChange() {
  scheduleRatioUpdate();
  window.setTimeout(() => {
    playerStore.updateRatios();
  }, 120);
}

function handleVisibilityGeometryChange() {
  if (!document.hidden) {
    handleViewportGeometryChange();
  }
}

const currentSrcString = computed(() => {
  return typeof $src.value === "string" ? $src.value : $src.value?.src || "";
});

const loop = route.query.loop == "1";
const minimal = route.query.minimal == "1";

const playbackStore = PlaybackStore();
const mountTime = ref(null);
const loadStartTime = ref(null);
const lastWistiaRefreshSignature = ref("");
const debugWindow = window as Window & {
  annStoreDebug?: Record<string, unknown>;
  doAnswers?: (options?: { includeHidden?: boolean }) => Array<Record<string, unknown>>;
};

function stripHtml(value: unknown): string {
  if (value == null) {
    return "";
  }

  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeStringList(value: unknown): string[] {
  if (value == null || value === "") {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) => normalizeStringList(entry));
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      try {
        return normalizeStringList(JSON.parse(trimmed));
      } catch (_error) {
        // Fall back to the raw string when legacy data is not valid JSON.
      }
    }

    return [stripHtml(trimmed)];
  }

  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap((entry) =>
      normalizeStringList(entry)
    );
  }

  return [String(value)];
}

function normalizeQuestionAnswers(value: unknown): Array<Record<string, unknown>> {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(
      (answer): answer is Record<string, unknown> =>
        typeof answer === "object" && answer !== null
    );
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.filter(
            (answer): answer is Record<string, unknown> =>
              typeof answer === "object" && answer !== null
          )
        : [];
    } catch (_error) {
      return [];
    }
  }

  return [];
}

function formatCorrectAnswer(question: Record<string, unknown>): string | null {
  const singleCorrect = stripHtml(question.question_single_correct);
  const multiCorrect = normalizeStringList(question.question_multi_correct);
  const questionType = String(question.question_type || "");

  if (["multiple", "select", "date"].includes(questionType)) {
    return singleCorrect || null;
  }

  if (questionType === "number") {
    const [minValue, maxValue] = multiCorrect;
    if (!minValue) {
      return null;
    }

    if (!maxValue || maxValue === minValue) {
      return minValue;
    }

    return `${minValue} - ${maxValue}`;
  }

  if (questionType === "all") {
    const choices = normalizeQuestionAnswers(question.question_answers)
      .map((answer) => stripHtml(answer.text))
      .filter(Boolean);
    const validAnswers = multiCorrect.filter((answer) => {
      return choices.length === 0 || choices.includes(answer);
    });
    const answers = validAnswers.length > 0 ? validAnswers : multiCorrect;
    return answers.length > 0 ? answers.join(", ") : null;
  }

  if (multiCorrect.length > 0) {
    return multiCorrect.join(", ");
  }

  return singleCorrect || null;
}

function buildQuestionAnswerRows(includeHidden = false) {
  const orderedAnnotations = includeHidden ? annStore.mainAnns : annStore.visibleMainAnns;

  return orderedAnnotations
    .filter((annotation) => annotation.type_of === "question")
    .map((question, index) => {
      const correctAnswer = formatCorrectAnswer(question);
      const answerChoices = normalizeQuestionAnswers(question.question_answers)
        .map((answer) => stripHtml(answer.text))
        .filter(Boolean);

      return {
        index: index + 1,
        id: question.id,
        time: Number(question.exact_time || 0),
        type: question.question_type || null,
        prompt: stripHtml(
          question.transformed_question_prompt || question.question_prompt || ""
        ),
        correct_answer: correctAnswer,
        answer_choices: answerChoices,
        has_correct_answer: Boolean(correctAnswer),
      };
    });
}

function doAnswers(options: { includeHidden?: boolean } = {}) {
  const rows = buildQuestionAnswerRows(Boolean(options.includeHidden));

  if (rows.length === 0) {
    console.warn("No question annotations found for this video.");
    return rows;
  }

  console.table(
    rows.map((row) => ({
      index: row.index,
      time: row.time,
      type: row.type,
      prompt: row.prompt,
      correct_answer: row.correct_answer || "",
      answer_choices: row.answer_choices.join(" | "),
    }))
  );

  return rows;
}

watch(
  () => videoStore.video.source,
  (newSource) => {
    const newSrc = getVideoSource({ source: newSource });
    if (newSrc !== $src.value) {
      $src.value = newSrc;
      log.info(1,"🌜 Player source changed", newSrc)
      // Reset video element reference when source changes to ensure duration is updated
      playerStore.resetVideoElement();
      playerStore.editReturnTime = 0.0
      playerStore.lastCurrentTime = 0.0
      annStore.resetState()

      // Re-initialize text tracks after source change to preserve caption state
      setTimeout(() => {
        playerStore.initializeTextTracks(route.query.captions_track);
      }, 500);
    }
  },
  { deep: true } // Use deep watching if video is an object with nested properties
);

watch(
  () => `${videoStore.video?.source?.id || ""}:${videoStore.video?.source?.key || ""}`,
  () => {
    lastWistiaRefreshSignature.value = "";
  }
);

const throttledSetPlayerState = throttle((state) => {
  // log.info(1,"🌜 Player throttledSetPlayerState", state)
  playerStore.setPlayerState(state)
}, 20)

onMounted(() => {
  log.info(1,"Player onMounted EditReturnTime/ editReturnToken", playerStore.editReturnTime, playerStore.editReturnToken)
  // setTimeout(() => {
  //   if (!threadMonitor.value && playerStore.isDebugPerformance) {
  //     threadMonitor.value = new MainThreadMonitor({ threshold: 16 })
  //     threadMonitor.value.start()
  //     window.mainThreadMonitor = threadMonitor.value;
  //     window.mainThreadMonitor.wrapObject(annStore, 'annStore.');
  //     window.mainThreadMonitor.wrapObject(playbackStore, 'playbackStore.');
  //     log.info(1,"🌜 Player started threadMonitor", threadMonitor.value)
  //   } else {
  //     log.info(1,"🌜 Player no threadMonitor")
  //   }
  // }, 5000)
  mountTime.value = performance.now();
  // log.info(1,"🌜 Player onMounted", playerStore.available);
  if (playerStore.isDebug) {
    $player.value.controlsDelay = 100000;
  } else if (videoStore.video.manage) {
    $player.value.controlsDelay = 5000;
  } else {
    $player.value.controlsDelay = 2500;
  }
  // Always set loop=true to prevent ended state and maintain seekability
  $player.value.loop = true;
  if (!videoStore.video.source) {
    log.info(1,"⚠️ No video source available in videoStore");
    return;
  }
  playerStore.error = null;

  // Initialize the video source
  $src.value = getVideoSource(videoStore.video);
  // log.info(1,"🌜 Player $src.value", $src.value);
  // $src.value = 'youtube/_cMxraX_5RE'

  // Update the player store
  playerStore.activeSrc = typeof $src.value === "string" ? $src.value : $src.value?.src;
  log.info(1,"🌜 Player $src.value", $src.value);
  playerStore.player = $player.value;
  playerStore.refPlayer = $player;
  setTimeout(() => {
    playerStore.updateRatios()
  }, 200)  

  // $player.value.keyShortcuts = {
  //   fooBar: {
  //     keys: ['Space'],
  //     onKeyUp({ event, player, remote }) {
  //       log.info(1,"🌜 Player custom onKeyUp", event)
  //       playerStore.togglePlayer("player handleKeyDown")
  //     },
  //   }
  // }

  // Subscribe to player state changes
  $player.value?.subscribe((state) => {
    const {
      currentSrc,
      canFullscreen,
      controlsVisible,
      controlsHidden,
      fullscreen,
      playing,
      qualities,
      quality,
      seekableEnd,
      seekableStart,
      textTrack,
      currentTime,
      volume,
      waiting,
      duration,
      ended,
      error,
      muted,
      paused,
      playbackRate,
      seeking,
      started,
      width,
      mediaWidth,
      height,
      mediaHeight,
    } = state;

    const obj = {
      currentSrc,
      canFullscreen,
      controlsHidden,
      fullscreen,
      playing,
      qualities,
      quality,
      seekableEnd,
      seekableStart,
      textTrack,
      volume,
      currentTime,
      waiting,
      controlsVisible,
      duration,
      ended,
      error,
      muted,
      paused,
      playbackRate,
      seeking,
      started,
      width,
      mediaWidth, 
      height,
      mediaHeight,
    };
    throttledSetPlayerState(obj)
    if (
      obj.textTrack &&
      obj.textTrack.label &&
      props.isPlayback &&
      playbackStore.captions != obj.textTrack.label
    ) {
      // log.info(1,"🌜 Player textTrack", obj.textTrack)
      if (obj.textTrack.mode == "showing") {
        log.info(1,"🌜 ActiveTextTrack is now", obj.textTrack.label);
        playbackStore.captions = obj.textTrack.label;
      }
      if (obj.textTrack.language?.includes("autogen")) {
        log.info(1,"Remove autogenerated text track", obj.textTrack.label);
        removeAllTextTracks();
      }
    }
    // playbackStore.handleUpdate(obj)
  });

  if (props.isEnd) {
    return;
  }

  if (videoStore.video.design && videoStore.video.design.font_family) {
    // log.info(1,
    //   "🌜 Player onMounted font for " +
    //     videoStore.video.title +
    //     ": " +
    //     videoStore.video.design.font_family
    // );

    // Check for custom_font array first (multiple font files with weights)
    if (videoStore.video.design.custom_font && videoStore.video.design.custom_font[videoStore.video.design.font_family]){
      let cf = videoStore.video.design.custom_font[videoStore.video.design.font_family]
      log.info(1,"🌜 Player loading custom fonts", JSON.parse(JSON.stringify(cf)))
      
      const weightMap = {
        "thin": "100",
        "Thin": "100",
        "extralight": "200",
        "Extra Light": "200",
        "light": "300",
        "Light": "300",
        "regular": "400",
        "Regular": "400",
        "normal": "400",
        "Normal": "400",
        "medium": "500",
        "Medium": "500",
        "semibold": "600",
        "Semi Bold": "600",
        "SemiBold": "600",
        "bold": "700",
        "Bold": "700",
        "extrabold": "800",
        "Extra Bold": "800",
        "ExtraBold": "800",
        "black": "900",
        "heavy": "900",
        "Heavy": "900",
        "Black": "900"
      };
      
      // Load all font variations
      const loadCustomFonts = async () => {
        const fontPromises = cf.map(async (entry) => {
          try {
            const weight = weightMap[entry.weight] || '400'
            log.info(1,`🌜 Loading font: ${entry.weight} -> ${weight}`, entry.url)
            
            // Use FontFace API for better loading control
            const font = new FontFace(
              videoStore.video.design.font_family,
              `url('${entry.url}')`,
              { 
                weight,
                style: 'normal',
                display: 'swap'
              }
            )
            
            await font.load()
            document.fonts.add(font)
            log.info(1,`✅ Font loaded: ${entry.weight}`)
            return true
          } catch (error) {
            log.error(`❌ Failed to load font ${entry.weight}:`, error)
            return false
          }
        })
        
        // Wait for all fonts to load
        const results = await Promise.all(fontPromises)
        const successCount = results.filter(r => r).length
        log.info(1,`🌜 Loaded ${successCount}/${cf.length} font variations`)
        
        // Apply font to containers after loading
        setTimeout(() => {
          StyleService.setFontName(videoStore.video.design.font_family)
          // Verify font is applied
          const testElement = document.querySelector('.text-content')
          if (testElement) {
            const computedStyle = window.getComputedStyle(testElement)
            log.info(1,`🌜 Applied font family: ${computedStyle.fontFamily}`)
          }
        }, 100)
      }
      
      // Execute the async function
      loadCustomFonts()
    }
    
    // Fallback to single font link (for backwards compatibility)
    else if (videoStore.video.design.font_link) {
      StyleService.loadAndApplyFont(
        videoStore.video.design.font_family,
        videoStore.video.design.font_link
      );
    }
  } else {
    // log.info(1,"🌜 Player onMounted: no font");
  }

  // Initialize text tracks

  playerStore.initializeTextTracks(route.query.captions_track);

  // Handle autoplay
  handleAutoplay(videoStore.video);


  // Hack to get media-player to stop stealing focus when typing in other inputs
  const mediaPlayer = document.getElementById('media-player');
  mediaPlayer.setAttribute('tabindex', '-1');

  const originalFocus = mediaPlayer.focus?.bind(mediaPlayer);
  mediaPlayer.focus = function() { /* no-op: never allow */ };

  document.addEventListener('focusin', (e) => {
    if (!clickStore.hasActiveDialog) {
      log.info(2,"🌜 Player mediaPlayer focusin, !hasActiveDialog, return")
      return;
    }
    if (e.target === mediaPlayer || mediaPlayer.contains(e.target)) {
      log.info(2,"🌜 Player mediaPlayer focusin", e, "target", e.target, "contains", mediaPlayer.contains(e.target))
      e.stopImmediatePropagation();
      mediaPlayer.blur?.();
      // push focus to body (or somewhere you prefer) 
      setTimeout(() => document.body.focus({ preventScroll: true }), 0);
      log.info(2,"🌜 Player mediaPlayer focusin, pushed focus to body")
    }
  }, true);


  window.addEventListener("fullscreenchange", (event) => {
    log.info(1,"🌜 Player fullscreenchange", document.fullscreenElement)
    if (document.fullscreenElement) {
      playerStore.isFullscreen = true
    } else {
      playerStore.isFullscreen = false
    }
  })  

  window.addEventListener("keydown", handleKeyDown, {
    capture: true,        // runs before target/bubbling listeners
    passive: false        // lets you call preventDefault if needed
  });
  // $player.value?.addEventListener("keydown", playerKeyDown, true);
  // log.info(1,"🌜 Player onMounted handleKeyDown");
  window.addEventListener("blur", handleBlur);
  window.addEventListener("focus", handleFocus);
  window.addEventListener("resize", handleViewportGeometryChange, { passive: true });
  window.addEventListener("orientationchange", handleViewportGeometryChange, { passive: true });
  window.addEventListener("pageshow", handleViewportGeometryChange);
  document.addEventListener("visibilitychange", handleVisibilityGeometryChange);

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", handleViewportGeometryChange);
    window.visualViewport.addEventListener("scroll", handleViewportGeometryChange);
  }

  if (videoStore.video.use_afk) {
    // Throttle mousemove event to run at most once per second
    let lastAfkReset = 0;
    window.addEventListener("keydown", () => {
      const now = Date.now();
      if (now - lastAfkReset >= 1000) {
        lastAfkReset = now;
        resetAfkTimer();
      }
    });
    window.addEventListener("mousemove", () => {
      const now = Date.now();
      if (now - lastAfkReset >= 1000) {
        lastAfkReset = now;
        resetAfkTimer();
      }
    });
  }

  playerStore.initialized = true;
  applyIOSOverflowFix();
  applyEmbedWheelPassThroughFix();
  
  // Expose debugging methods to window for console access
  if (playerStore.isDebug || window.VITE_DEVELOPMENT || videoStore.video.manage) {
    debugWindow.annStoreDebug = {
      getMissedUpdates: () => annStore.getMissedUpdatesReport(),
      getActive: () => annStore.getActive(),
      clearMissedUpdates: () => annStore.clearMissedUpdates(),
      getMissedUpdatesArray: () => annStore.missedUpdates,
      doAnswers,
      logMissedUpdates: () => {
        const report = annStore.getMissedUpdatesReport();
        console.table(report.recentUpdates);
        log.info(1,"📊 Missed Updates Summary:", report);
      },
      // Test missed interaction recovery
      simulateMissedTime: (startTime, endTime) => {
        log.info(1,`🧪 Simulating missed time range: ${startTime}s → ${endTime}s`);
        annStore.processMissedTimeRange(startTime, endTime, true); // Include start time for simulation
      },
      // Get current active state
      getActiveState: () => ({
        active: Array.from(annStore.active.keys()),
        timeMap: Object.keys(annStore.timeMap).filter(key => annStore.timeMap[key].show?.length > 0),
        currentTime: playerStore.decitime
      })
    };
    debugWindow.doAnswers = doAnswers;
    log.info(1,"🔧 Debug methods available: window.annStoreDebug, window.doAnswers");
  }
});

const resetAfkTimer = () => {
  if (!playerStore.started || !videoStore.video.use_afk) {
    return;
  }
  if (videoStore.video.use_afk && !videoStore.video.manage) {
    playbackStore.startAfkTimer();
  }
};

onUnmounted(() => {
  log.info(1,"🌜 Player onUnmounted");
  window.removeEventListener("keydown", handleKeyDown);
  window.removeEventListener("keydown", playerKeyDown, true);
  window.removeEventListener("blur", handleBlur);
  window.removeEventListener("focus", handleFocus);
  window.removeEventListener("resize", handleViewportGeometryChange);
  window.removeEventListener("orientationchange", handleViewportGeometryChange);
  window.removeEventListener("pageshow", handleViewportGeometryChange);
  document.removeEventListener("visibilitychange", handleVisibilityGeometryChange);

  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", handleViewportGeometryChange);
    window.visualViewport.removeEventListener("scroll", handleViewportGeometryChange);
  }

  if (ratioUpdateFrame !== null) {
    window.cancelAnimationFrame(ratioUpdateFrame);
    ratioUpdateFrame = null;
  }
  if (disposeHlsInstanceListener) {
    disposeHlsInstanceListener();
    disposeHlsInstanceListener = null;
  }
  removeIOSOverflowFix();
  removeEmbedWheelPassThroughFix();

  window.removeEventListener("mousemove", resetAfkTimer);
  window.removeEventListener("keydown", resetAfkTimer);
  
  // Clear any pending seek timeouts in annStore
  annStore.clearIntentionalSeek();

  if (debugWindow.doAnswers === doAnswers) {
    delete debugWindow.doAnswers;
  }
  
  playerStore.initialized = false;
});

function getInfo() {
  let isEmbed = window !== window.parent;
  let isDev = window.VITE_DEVELOPMENT;
  let isIOS = browserInfo.isIOS;
  let isDebug = playerStore.isDebug || isDev;
  let pauseInactive = videoStore.video.pause_inactive;
  return { isEmbed, isIOS, pauseInactive, isDev, isDebug };
}

function handleBlur(event) {
  // log.info(1,"🌜 Player handleBlur", event);
  let { isEmbed, isIOS, pauseInactive, isDev, isDebug } = getInfo();
  // if (isDebug) {
  //   return;
  // }
  if (isIOS) {
    playerStore.pause();
  } // Always pause on iOS
  else if (isEmbed) {
    return;
  } // Don't pause if the player is embedded
  else if (pauseInactive) {
    playerStore.pause();
  } // Don't pause if the player is on iOS
}

function handleFocus(event) {
  // log.info(1,"🌜 Player handleFocus", event, clickStore.hasActiveDialog);
  // Only focus if setting is turned on
  const annStore = AnnStore()
  const clickStore = ClickStore()
  if (clickStore.magicMenuActive) {
    log.info(1,"🌜 Player handleFocus magicMenuActive, no focus");
    return;
  }
  if (!clickStore.hasActiveDialog && !videoStore.video.manage) {
    // Don't reset active if we have an active dialog
    annStore.resetActive(playerStore.decitime, "handleFocus")
  }
  const shouldResume = videoStore.video.resume_focus 
    && !videoStore.video.manage 
    && playerStore.started
    && !annStore.pausedInteraction
    && !clickStore.hasActiveDialog
  if (shouldResume) {
    let { isEmbed, isIOS, pauseInactive, isDev, isDebug } = getInfo();
    // if (isDebug) {
    //   return;
    // }
    if (isIOS) {
      return;
    } // Don't resume on iOS
    else if (isEmbed) {
      return;
    } // Don't resume if the player is embedded
    else if (videoStore.video.manage) {
      return;
    } // Don't resume if we are editing
    else {
      playerStore.playIfStarted();
    } // Resume if setting is on and not iOS or embedded
  }
}
function playerKeyDown(event) {
  // log.info(1,"🌜 Player playerKeyDown", event)
  if (event.code === "Tab") {
    return;
  }
  

  // event.preventDefault()
  // event.stopPropagation()
  // event.stopImmediatePropagation();
}
function handleKeyDown(event) {
  // log.info(1,"🌜 Player handleKeyDown", event)
  if (annStore.isEditing) {
    log.info(1,"🌜 Player handleKeyDown annStore.isEditing, no key");
    // event.preventDefault();
    // event.stopPropagation();
    return;
  }

  const tagName = event.target.tagName.toLowerCase();
  const isInput = ["input", "textarea", "select"].includes(tagName);
  const isContentEditable = event.target.isContentEditable;

  // Skip if the user is typing in a field
  if (isInput || isContentEditable || clickStore.hasActiveDialog) {
    // log.info(1,"🌜 Player handleKeyDown isInput or isContentEditable or hasActiveDialog", event.target.id)
    // event.stopPropagation();
    return;
  }

  if (clickStore.hasActiveDialog) {
    // log.info(1,"🌜 Player handleKeyDown hasActiveDialog, no key");
    event.stopPropagation();
    event.stopImmediatePropagation();
    return;
  }
  

  // log.info(1,
  //   "🌜 Player handleKeyDown tagName", event.code, event.key,
  //   tagName,
  //   isInput,
  //   isContentEditable
  // );
  // log.info(1,"🌜 Player handleKeyDown CONTINUE", event.code, isInput, isContentEditable, clickStore.hasActiveDialog)

  const skipAmount =
    videoStore.video.skip_amount || videoStore.video.org_skip_amount || 5;

  if (event.code === "Space" || event.key === " ") {
    // Let Vidstack handle spacebar (play/pause)
    if (videoStore.video.block_pausing && !videoStore.video.manage && playerStore.playerState.playing) {
      event.preventDefault()
      event.stopPropagation()
      return;
    }
    playerStore.togglePlayer("player handleKeyDown Space");
    event.stopImmediatePropagation()
    return;
  }

  if (event.code === "Enter" || event.code === "Space") {
    // log.info(1,"🌜 Window handleKeyDown Enter", event.target.id)
    if (event.target.id === "fast-forward-button" || event.target.id === "rewind-button" || event.target.id === "participate-button") {
      if (event.target.id === "fast-forward-button") {
        playerStore.fastForward();
      } else if (event.target.id === "rewind-button") {
        playerStore.rewind();
      } else if (event.target.id === "participate-button") {
        clickStore.startParticipate();
      }
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      return;
    }
    
  }

  if (event.code === "ArrowLeft" && !event.target.id.includes("volume-slider")) {
    log.info(1,"🌜 Player handleKeyDown ArrowLeft", event.target.id, canSeek.value)
    if (canSeek.value) {
      playerStore.rewind();
    }
    event.preventDefault();
    event.stopPropagation();
  } else if (event.code === "ArrowRight" && !event.target.id.includes("volume-slider")) {
    if (canSeek.value) {
      playerStore.fastForward();
    }
    event.preventDefault();
    event.stopPropagation();
  }

  // event.stopImmediatePropagation()
}

function onProviderChange(event: MediaProviderChangeEvent) {
  // log.info(1,"🌜 Player onProviderChange", event);
  if (disposeHlsInstanceListener) {
    disposeHlsInstanceListener();
    disposeHlsInstanceListener = null;
  }
  const provider = event.detail;
  // We can configure provider's here.
  if (isHLSProvider(provider)) {
    provider.config = {
      ...provider.config,
      // Prevent hls.js startup probe from forcing an initial low-quality fragment.
      testBandwidth: false,
      // Stay in auto mode after startup (we only pin the first fragment below).
      startLevel: -1,
    };
    disposeHlsInstanceListener = provider.onInstance((hls: any) => {
      const pinStartupQualityToHighest = () => {
        const levels = Array.isArray(hls?.levels) ? hls.levels : [];
        if (!levels.length) {
          return;
        }
        const highestLevelIndex = levels.length - 1;
        // Use highest level for startup, then allow ABR to continue in auto mode.
        hls.startLevel = highestLevelIndex;
        hls.nextAutoLevel = highestLevelIndex;
        log.info(1,
          "🌜 Player HLS startup quality pinned to highest",
          levels[highestLevelIndex]?.height || "unknown"
        );
      };

      if (typeof hls?.once === "function") {
        hls.once("hlsManifestParsed", pinStartupQualityToHighest);
      } else if (typeof hls?.on === "function") {
        const onManifestParsed = () => {
          pinStartupQualityToHighest();
          if (typeof hls?.off === "function") {
            hls.off("hlsManifestParsed", onManifestParsed);
          }
        };
        hls.on("hlsManifestParsed", onManifestParsed);
      }
    });
  }
}
function onLoadStart(event: MediaCanPlayEvent) {
  loadStartTime.value = performance.now();
  measuredDuration.value = 0;
  initialTimingProbeScheduled.value = false;
  logInitialTimingState("loadstart");
}
function onLoadedMetadata(event: MediaCanPlayEvent) {
  syncMeasuredDuration("loadedmetadata");
  scheduleInitialTimingProbes("loadedmetadata");
  log.info(1,"✅ Player onLoadedMetadata:", Math.round(performance.now() - loadStartTime.value), "ms load", Math.round(performance.now() - mountTime.value), "ms total");
}

function onWaiting(event: MediaCanPlayEvent) {
  seekStartTime.value = performance.now();
  log.info(1,"🟡 Player onWaiting...");
}

function tryAutoplay() {
  playerStore.attachVideoElement();
  if (playerStore.branching) {
    log.info(1,"🌜 Player tryAutoplay branching", playerStore.startTime)
    // Reset video element to ensure fresh duration after branching
    playerStore.resetVideoElement();

    // Delay setting branching = false until after annotations are cleared
    // This prevents old annotations from flashing when IntLayer becomes visible
    setTimeout(
      () => {
        playerStore.branching = false;
        if (playerStore.startTime) {
          playerStore.seekAndPlay(playerStore.startTime)
        } else {
          playerStore.play("tryAutoplay branching");
        }
      },
      videoStore.video.source.type_of === "youtube" ? 200 : 0
    );
    return
  } else if (props.autoplay) {
    log.info(1,"🌜 Player tryAutoplay autoplaying");
    playerStore.autoplayUnmuted = false;
    playerStore.toggleMute(true);
    setTimeout(() => {
      playerStore.play("tryAutoplay autoplaying");
    }, 1000)
  }

  if (props.time) {
    log.info(1,"🌜 Player tryAutoplay seeking to", props.time);
    playerStore.seek(props.time);
  }
}

function onAutoPlayFail(event) {
  log.info(1,"🌜 Player onAutoPlayFail", event);
} //

// We can listen for the `can-play` event to be notified when the player is ready.
function onCanPlay(event: MediaCanPlayEvent) {
  log.info(1,"✅ Player onCanPlay:", Math.round(performance.now() - mountTime.value), "ms");
  syncMeasuredDuration("canplay");
  scheduleInitialTimingProbes("canplay");
  
  // annStore.resetState();
  // Aggressive text track removal for iOS
  removeAllTextTracks();
  setTimeout(() => {
    playerStore.initializeTextTracks(route.query.captions_track);
  }, 500)
  setTimeout(() => {
    preloadManager.init(annStore.anns, playerStore.decitime)
  }, 1000)
  
  playerStore.ready = true;
  playerStore.error = null;
  playerStore.updateRatios()
  emitMessage('video_ready')
  tryAutoplay();
  if (props.isEnd) {
    const offset = 0;
    setTimeout(() => {
      log.info(1,
        "🌜 Player onLoadedMetadata isEnd",
        $player.value?.duration - offset
      );
      playerStore.seek($player.value?.duration - offset);
    }, 500);
  }

  playerStore.clipEndTime = playerStore.videoElement?.duration - 0.01;

  // Make sure duration is correct on video record
  let trueDuration = Math.floor(playerStore.videoElement?.duration || 0);
  if (
    !videoStore.video.duration ||
    (videoStore.video.duration != trueDuration && trueDuration > 0)
  ) {
    videoStore.updateDuration();
  }
  setTimeout(() => {
    if (!props.autoplay && !props.time) {
      playerStore.setVolume(localStorage.getItem("volume") || playerStore.originalVolume || 1)
    } else if (props.autoplay && !playerStore.started) {
      log.info(1,"🌜 Player onCanPlay autoplay and playerStore.started is false, setting volume to 0")
      playerStore.setVolume(0)
    }
  }, 50)
  if (props.isEditor && playerStore.editReturnToken == videoStore.video?.token) {
    setTimeout(() => {
      log.info(1,"🌜 Player onCanPlay isEditor, seeking to return time", playerStore.editReturnTime)
      playerStore.seek(playerStore.editReturnTime || 0)
    }, 200)
  }
  if (playerStore.speed != 1.0) {
    setTimeout(() => {
      if (playerStore.videoElement) {
        playerStore.videoElement.playbackRate = playerStore.speed;
      }
      log.info(1,"🌜 Player onCanPlay speed", playerStore.speed)
    }, playerStore.videoElement ? 0 : 1000)
  }

}

function onTimeUpdate(event: MediaTimeUpdateEvent) {
  if (playerStore.branching) {
    log.info(1,"🌜 Player onTimeUpdate branching, resetting lastCurrentTime");
    playerStore.lastCurrentTime = 0;
    return;
  }
  
  // Check for video source changes (branching)
  playerStore.checkVideoSourceChange(playerStore.currentSrc);
  
  // Handle video end detection with loop=true strategy
  if (playerStore.safeDuration) {
    const currentTime = event.detail.currentTime;
    const endThreshold = playerStore.safeDuration - 0.05;
    const seekAwayThreshold = 2.0; // If user seeks 2+ seconds away from end, allow end trigger again
    
    // Check if we've reached the end
    if (currentTime >= endThreshold && !playerStore.hasTriggeredEnd) {
      if (annStore.isEditing) {
        log.info(1,"🌜 Player onTimeUpdate isEditing, no end");
        return;
      }
      
      // Check if this is an intentional loop (user wants actual looping)
      if (route.query.loop == "1") {
        log.info(1,"🌜 Player onTimeUpdate intentional loop, allowing restart");
        return; // Let it loop naturally
      }
      
      // Check for missed switch annotations in the last 0.3 seconds
      const missedSwitchWindow = 0.3;
      const checkFromTime = Math.round(playerStore.safeDuration - missedSwitchWindow);
      
      log.info(1,`🌜 Player onTimeUpdate checking for missed switch annotations from ${checkFromTime}s to ${currentTime}s`);
      
      // Find any switch annotations that should have triggered
      const missedSwitches = [];
      for (let checkTime = checkFromTime; checkTime <= playerStore.safeDuration + 0.3; checkTime += 0.1) {
        // Round to 1 decimal place to avoid floating point precision issues
        const roundedCheckTime = Math.round(checkTime * 10) / 10;
        log.info(1,`🌜 Player onTimeUpdate checking for missed switch annotations at ${roundedCheckTime}s`);
        const timeKey = convertDecitimeToKey(roundedCheckTime);
        const interactions = annStore.timeMap[timeKey];
        
        if (interactions && interactions.show) {
          const switchAnns = interactions.show.filter(ann => 
            (ann.type_of === 'switch' || ann.type_of === 'pause' || ann.pause) && 
            !annStore.isDoneAt(ann.exact_time, ann.id) &&
            ann.exact_time >= checkFromTime && ann.visible && !ann.hidden
          );
          
          if (switchAnns.length > 0) {
            missedSwitches.push(...switchAnns);
          }
        }
      }
      
      // Process any missed switch annotations
      if (missedSwitches.length > 0) {
        log.info(1,`🌜 Player onTimeUpdate found ${missedSwitches.length} missed switch annotations, processing...`);
        
        // Process the first switch annotation (switches are typically singular)
        const switchAnn = missedSwitches[0];
        log.info(1,`🌜 Player onTimeUpdate executing missed switch: ${switchAnn.content} at ${switchAnn.exact_time}s`);
        
        // Execute the switch annotation
        annStore.doShow(switchAnn);
        
        // Don't trigger end if we're switching videos
        return;
      }
      
      emitMessage('progress_95')
      emitMessage('progress_100')
      emitMessage('video_end')
      // Mark that we've triggered the end to prevent multiple triggers
      playerStore.hasTriggeredEnd = true;
      
      log.info(1,`🌜 Player onTimeUpdate trigger video end at ${currentTime}s`);
      let mustClickActives = Array.from(annStore.active.values()).filter(ann => ann.visible && ann.must_click && !ann.clicked && !ann.hidden)
      log.info(1,"🌜 Player onTimeUpdate mustClickActives", mustClickActives)
      // Check for pause until click interactions
      if (mustClickActives.length > 0) {
        log.info(1,"🌜 Player onTimeUpdate pause until click interactions, not pausing");
        annStore.doMustClick(mustClickActives[0])
        playerStore.pause("onTimeUpdate video end, pause until click");
        return;
      }
      playerStore.pause("onTimeUpdate video end");
      clickStore.handleVideoEnd(videoStore.video);
      
      // Pause at the end position but don't seek (maintains seekability)
      return;
    }
    
    // Reset end trigger if user has seeked significantly away from the end
    // This allows multiple end triggers in the same video
    if (playerStore.hasTriggeredEnd && currentTime < endThreshold - seekAwayThreshold) {
      log.info(1,`🌜 Player onTimeUpdate user seeked away from end, re-enabling end trigger`);
      playerStore.resetEndDetection('user seeked away');
    }
    
    // Check for actual loop event (currentTime jumped back to start)
    if (playerStore.lastCurrentTime && currentTime < 1.0 && playerStore.lastCurrentTime > endThreshold) {
      log.info(1,"🌜 Player onTimeUpdate detected loop restart, pausing at end");
      log.info(1,playerStore.lastCurrentTime, currentTime, endThreshold)
      playerStore.seek(endThreshold);
      playerStore.pause("onTimeUpdate loop prevention");
      if (!playerStore.hasTriggeredEnd) {
        playerStore.hasTriggeredEnd = true;
        emitMessage('video_end')
        clickStore.handleVideoEnd(videoStore.video);
      }
      return;
    }
    playerStore.lastCurrentTime = currentTime;
  }

  playerStore.handleTimeUpdate(event.detail.currentTime);
  if (props.isPlayback) {
    playbackStore.handleTimeUpdate(event.detail.currentTime);
  }
}

const getCrossOrigin = computed(() => {
  if (uploadStore.uploadFile) {
    return "anonymous";
  }
  const src = currentSrcString.value;
  if (!src) {
    return "anonymous";
  }
  // log.info(1,"🌜 Player getCrossOrigin", src)
  if (src.includes("cloudfront")) {
    return false;
  } else if (src.includes("panopto")) {
    return "anonymous";
  } else if (src.includes("mindstamp.com")) {
    // Bunny CDN links served via mindstamp.com lack CORS headers; skip crossorigin attribute
    return false;
  }
  return "anonymous";
  // const val = $src.value && $src.value.src && ($src.value.src.includes('synthesia') || $src.value.src.includes('panopto')) ? 'use-credentials' : $src.value.includes("cloudfront") ? false : 'anonymous'
  // log.info(1,"🌜 Player getCrossOrigin", $src.value, val)
  // return val
});

const getThumbnails = computed(() => {
  log.info(1,"🌜 Player getThumbnails", videoStore.video.has_thumbnails)
  const result = videoStore.video.has_thumbnails
    ? `/backend/videos/${videoStore.video.id}/thumbnails`
    : false;
  // log.info(1,"🌜 Player getThumbnails result", result)
  return result;
})

const isNormal = computed(() => {
  return !props.isEnd && !props.isMenu;
});
const loadStrategy = computed(() => {
  if (playerStore.isMobile && !playerStore.isWebView) {
    return "idle";
  }
  return "eager";
});
const measuredDuration = ref(0);
const providedDuration = computed(() => {
  const storedDuration = Number(videoStore.video?.duration || videoStore.video?.source?.duration || 0);
  const nativeDuration = Number.isFinite(measuredDuration.value) ? measuredDuration.value : 0;
  return Math.max(storedDuration, nativeDuration, 0);
});
const initialTimingProbeScheduled = ref(false);

function syncMeasuredDuration(source = "unknown") {
  playerStore.attachVideoElement();
  const duration = Number(playerStore.videoElement?.duration || $player.value?.duration || 0);
  if (!Number.isFinite(duration) || duration <= 0) {
    return;
  }
  if (Math.abs(duration - measuredDuration.value) < 0.01) {
    return;
  }
  measuredDuration.value = duration;
  log.info(1, "🌜 Player syncMeasuredDuration", source, duration);
}

function logInitialTimingState(source = "unknown") {
  playerStore.attachVideoElement();
  const videoElement = playerStore.videoElement;
  const nativeDuration = Number(videoElement?.duration || 0);
  const nativeCurrentTime = Number(videoElement?.currentTime || 0);
  const nativeSeekableEnd =
    videoElement?.seekable && videoElement.seekable.length > 0
      ? Number(videoElement.seekable.end(videoElement.seekable.length - 1))
      : 0;
  const nativeBufferedEnd =
    videoElement?.buffered && videoElement.buffered.length > 0
      ? Number(videoElement.buffered.end(videoElement.buffered.length - 1))
      : 0;

  log.info(1, "🌜 Player initial timing probe", source, {
    token: videoStore.video?.token,
    sourceType: videoStore.video?.source?.type_of,
    sourceKey: videoStore.video?.source?.key,
    storedDuration: Number(videoStore.video?.duration || 0),
    providedDuration: Number(providedDuration.value || 0),
    measuredDuration: Number(measuredDuration.value || 0),
    nativeDuration,
    nativeCurrentTime,
    nativeSeekableEnd,
    nativeBufferedEnd,
    readyState: Number(videoElement?.readyState || 0),
    networkState: Number(videoElement?.networkState || 0),
    playerDuration: Number(playerStore.playerState?.duration || 0),
    playerCurrentTime: Number(playerStore.playerState?.currentTime || 0),
    playerSeekableEnd: Number(playerStore.playerState?.seekableEnd || 0),
    playerSeekableStart: Number(playerStore.playerState?.seekableStart || 0),
  });
}

function scheduleInitialTimingProbes(source = "unknown") {
  if (initialTimingProbeScheduled.value) {
    return;
  }
  initialTimingProbeScheduled.value = true;
  const checkpoints = [
    { label: `${source}:immediate`, delay: 0 },
    { label: `${source}:250ms`, delay: 250 },
    { label: `${source}:1000ms`, delay: 1000 },
    { label: `${source}:2500ms`, delay: 2500 },
  ];
  checkpoints.forEach(({ label, delay }) => {
    setTimeout(() => {
      syncMeasuredDuration(label);
      logInitialTimingState(label);
    }, delay);
  });
}

function normalizeUploadKey(value: unknown) {
  if (value === null || value === undefined) {
    return "";
  }
  const asString = String(value);
  if (asString.length === 0) {
    return "";
  }
  try {
    return decodeURIComponent(asString.replace(/\+/g, "%20"));
  } catch {
    return asString;
  }
}

function guessUploadMimeType(filename: unknown, fallbackType: unknown) {
  const normalizedFallback = typeof fallbackType === "string" ? fallbackType : "";
  if (normalizedFallback) {
    return normalizedFallback;
  }
  const lower = String(filename || "").toLowerCase();
  if (lower.endsWith(".m3u8")) {
    return "application/x-mpegURL";
  }
  if (lower.endsWith(".mp4")) {
    return "video/mp4";
  }
  return "video/mp4";
}

function buildUploadSource() {
  const localSrc = uploadStore.uploadSource?.src;
  if (!localSrc) {
    return null;
  }
  const localType = guessUploadMimeType(
    uploadStore.uploadSettings.key,
    uploadStore.uploadSource?.type || uploadStore.uploadFile?.type
  );
  return {
    src: localSrc,
    type: localType,
  };
}

// Method to extract the video source from the video object
function getVideoSource(video) {
  // log.info(1,
  //   "🌜 Player getVideoSource",
  //   video.source.hls,
  //   video.source.mp4,
  //   video.source.s3,
  //   video.source.youtube
  // );
  // log.info(1,
  //   "🌜 Player getVideoSource",
  //   uploadStore.uploadFile,
  //   uploadStore.uploadSource,
  //   uploadStore.uploadSource.src
  // );

  const source = video?.source || {};
  const uploadKey = normalizeUploadKey(uploadStore.uploadSettings.key);
  const sourceKey = normalizeUploadKey(source.key);
  const hasUploadSrc = !!uploadStore.uploadFile && !!uploadStore.uploadSource?.src;
  const keyMatches = !!uploadKey && !!sourceKey && uploadKey == sourceKey;
  const bunnyIdMatches =
    !!uploadStore.uploadSettings.bunnyId &&
    !!source.bunny_id &&
    uploadStore.uploadSettings.bunnyId == source.bunny_id;
  const bunnyVideoIdMatches =
    !!uploadStore.uploadSettings.bunnyVideoId &&
    !!source.bunny_id &&
    uploadStore.uploadSettings.bunnyVideoId == source.bunny_id;
  const shouldPreferLiveUpload =
    uploadStore.isUploading && video?.manage && source.type_of == "upload";
  let shouldUseUpload =
    hasUploadSrc &&
    (keyMatches || bunnyIdMatches || bunnyVideoIdMatches || shouldPreferLiveUpload);
  log.info(1,
    "🌜 Player getVideoSource shouldUseUpload uploadKey",
    uploadStore.uploadSettings.key,
    uploadKey
  );
  log.info(1,
    "🌜 Player getVideoSource shouldUseUpload videoKey",
    source.key,
    sourceKey
  );
  log.info(1,"🌜 Player getVideoSource shouldUseUpload", shouldUseUpload);
  const uploadSource = shouldUseUpload ? buildUploadSource() : null;
  let result = uploadSource ||
      source.hls ||
      source.mp4 ||
      source.s3 ||
      source.youtube ||
      source.key ||
      "";
  // log.info(1,"🌜 Player getVideoSource result", result);
  if (
    typeof result === "string" &&
    result.includes("player.vimeo.com/external")
  ) {
    // Vidstack automatically tries to load Vimeo links as iframe
    // We need to add a query param to the url to force it to load as a video
    if (result.includes("?")) result += "&progressive_redirect=1";
    else result += "?progressive_redirect=1";
  }
  if (typeof result === "string") {
    const lower = result.toLowerCase();
    if (lower.endsWith(".m3u8")) {
      return { src: result, type: "application/x-mpegURL" };
    }
    if (lower.endsWith(".mp4")) {
      return { src: result, type: "video/mp4" };
    }
  }
  return result;
}

// Method to reset the player
async function resetPlayer() {
  if ($player.value) {
    try {
      await $player.value.pause();
      await $player.value.seek(0);
      playerStore.resetPlayerState();
      log.info(1,"🎥 Player reset successfully");
    } catch (error) {
      log.info(1,"❌ Error resetting player:", error);
    }
  }
}

const onError = (event) => {
  log.info(1,"🌜 Player onError", event);
  log.info(1,"🌜 Player backup_hls", videoStore.video.source.backup_hls);
  // playerStore.error = event.detail.message;
  emitMessage('playback_error', { src: $src.value, error: event.detail.message})
  const failedSrc =
    typeof $player.value?.src === "string" ? $player.value.src : currentSrcString.value;
  if (
    videoStore.video.source.backup_hls &&
    normalizeMediaUrl($player.value?.src) != normalizeMediaUrl(videoStore.video.source.backup_hls)
  ) {
    requestWistiaAssetRefresh(failedSrc);
    setTimeout(() => {
      log.info(1,
        "🌜 Player onError, switching to backup HLS",
        videoStore.video.source.backup_hls
      );
      // playerStore.error = null;
      $player.value.src = videoStore.video.source.backup_hls;
    }, 50);
  }
};

function requestWistiaAssetRefresh(failedSrc: string) {
  const source = videoStore.video?.source;
  if (!source?.id || !source?.wistia_id || !source?.key) {
    return;
  }
  const normalizedFailedSrc = normalizeMediaUrl(failedSrc);
  const normalizedSourceKey = normalizeMediaUrl(source.key);
  if (!normalizedFailedSrc || !normalizedSourceKey || normalizedFailedSrc != normalizedSourceKey) {
    return;
  }
  const signature = `${source.id}:${normalizedSourceKey}`;
  if (lastWistiaRefreshSignature.value == signature) {
    return;
  }
  lastWistiaRefreshSignature.value = signature;
  log.info(1,"🌜 Player requesting Wistia asset refresh", signature);
  videoStore.refreshWistiaAssetsFromPlaybackError(normalizedFailedSrc).then((response) => {
    log.info(1,"🌜 Player Wistia asset refresh result", response);
  }).catch((error) => {
    log.info(1,"🔴 Player Wistia asset refresh failed", error);
  });
}

function normalizeMediaUrl(url: string | undefined) {
  if (!url || typeof url !== "string") {
    return "";
  }
  return url.split("?")[0].split("#")[0];
}

// Method to handle autoplay based on the new video
function handleAutoplay(video) {
  if (props.autoplay) {
    log.info(1,"🚀 Autoplay enabled, playing the video");
    playerStore.autoplayUnmuted = false;
    playerStore.toggleMute(true);
    if (props.time) {
      playerStore.seek(props.time);
    }
    playerStore.play("player handleAutoplay");
  }
}

const seekStartTime = ref(null);
const onSeek = (event) => {
  seekStartTime.value = performance.now();
  log.info(1,"🌜 Player onSeek", event, event.timeStamp);
  annStore.resetActive(event.detail, "onSeek");
};
const onSeeked = (event) => { 
  log.info(1,"🌜 Player seek finished", Math.round(performance.now() - seekStartTime.value), "ms seek");
};
const onPlay = (event) => {
  log.info(1,"🌜 Player onPlay", event, event.timeStamp, "ShowVideoLayout: " + showVideoLayout.value);
  emitMessage('video_play')
  clickStore.clearTimedPause();
  playerStore.resetVolume()
  
  // Try removing tracks again on play for iOS
  removeAllTextTracks();
  if (videoStore.video.use_afk && !videoStore.video.manage) {
    playbackStore.startAfkTimer();
  }
};

const onPause = (event) => {
  // log.info(1,"🌜 Player onPause", event, event.timeStamp);
  if (!videoStore.video.id || videoStore.video.manage || !videoStore.video || !videoStore.video.design) {
    return;
  }
  emitMessage('video_pause')
  setTimeout(() => {
    if (
      videoStore.video.design.magic_menu.show_on_pause &&
      !clickStore.hasActiveDialog &&
      !clickStore.activeTimedPause
    ) {
      log.info(1,"ShouldShowMagicMenu");
      clickStore.showMagicMenu();
    }
  }, 100);
};

const onEnd = (event) => {
  log.info(1,"🌜 Player onEnd - preventing actual end to maintain seekability");
  
  // If video actually ends despite our prevention, reset to seekable position
  if (playerStore.safeDuration) {
    const seekablePosition = playerStore.safeDuration - 0.1;
    log.info(1,`🌜 Player onEnd - seeking to ${seekablePosition}s to restore seekability`);
    playerStore.seek(seekablePosition);
    playerStore.pause("onEnd recovery");
    
    // Trigger our custom end handling
    if (!loop) {
      clickStore.handleVideoEnd(videoStore.video);
    }
  }
}

const canSeek = computed(() => {
  if (videoStore.video.manage) {
    return true;
  }
  if (minimal) {
    return false;
  }

  if (videoStore.video.design.hide_controls) {
    return false;
  }

  if (!videoStore.video.design.seekbar_active) {
    return false;
  }
  if (!videoStore.video.design.show_seekbar) {
    return false;
  }
  return true;
});

const showVideoLayout = computed(() => {
  if (playerStore.isDebug) {
    return true;
  }
  if (!playerStore.player) {
    return false;
  }
  if (playerStore.branching) {
    return false;
  }

  if (clickStore.magicMenuActive) {
    return false;
  }
  if (clickStore.participateActive) {
    return false;
  }
  if (playerStore.error) {
    return false;
  }
  if (!playerStore.started) {
    return false;
  }
  if (annStore.isEditing) {
    return false;
  }
  if (props.isEnd) {
    return false;
  }
  if (!isNormal.value) {
    return false;
  }
  if (annStore.isBulkEditing) {
    return false;
  }
  if (!videoStore.video.id) {
    return false;
  }
  if (!videoStore.video.manage) {
    if (videoStore.video.design.hide_controls) {
      return false;
    }
  }
  if (clickStore.seekbarShouldBeHidden) {
    return false;
  }
  if (clickStore.hasActiveDialog) {
    return false;
  }
  if (annStore.pausedInteraction && !videoStore.video.manage) {
    return false;
  }
  if (minimal) {
    return false;
  }
  if (annStore.mustClickInteractions.length > 0) {
    return false;
  }
  return true;
});

const masterLayerStyle = computed(() => {
  // Keep intro/resume layers pinned to the full player box before playback starts.
  // iOS 26 can report unstable media geometry early, which can shift layerStyle.
  if (!playerStore.started) {
    if (browserInfo.isIOS && playerStore.isWebView && playerStore.layerStyle?.left && playerStore.layerStyle.left !== 0 && playerStore.layerStyle.left !== "0") {
      log.info(2, "🌜 Player pre-play master layer forcing full bounds over shifted layerStyle", playerStore.layerStyle);
    }
    return {
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
    };
  }
  return playerStore.layerStyle;
});

function throttleLeadingOnly(fn, delay) {
  let lastCall = 0;

  return function (...args) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  };
}

const doMouseMove = throttleLeadingOnly((e) => {
  $player.value.controls.show(0, e);
  $player.value.controls.resume();
  playerStore.updateRatios()
}, 500);

import Spinner from '@/front/components/Spinner.vue';

function removeAllTextTracks() {
  log.info(1, "Player: Aggressively removing all autogenerated text tracks");
  
  // Method 1: Remove via player API
  if ($player.value?.textTracks) {
    const tracks = [...$player.value.textTracks];
    for (const track of tracks) {
      if (track.language?.includes("autogen")) {
        track.mode = 'disabled';
        try {
          $player.value.textTracks.remove(track.id);
          log.info(1, "Player: Removed autogen track", track.label, track.language);
        } catch (error) {
          log.info(1, "Player: Error removing track", error);
        }
      }
    }
  }
  
  // Method 2: Direct DOM manipulation for iOS
  const video = document.querySelector('#media-player video') as HTMLVideoElement;
  if (video?.textTracks) {
    for (let i = 0; i < video.textTracks.length; i++) {
      const track = video.textTracks[i];
      if (track.language?.includes("autogen")) {
        track.mode = 'disabled';
        log.info(1, "Player: Disabled DOM autogen track", track.label, track.language);
      }
    }
  }
  
  // Method 3: Remove track elements from DOM
  const trackElements = document.querySelectorAll('#media-player track');
  trackElements.forEach(track => {
    const lang = track.getAttribute('srclang');
    if (lang?.includes("autogen")) {
      track.remove();
      log.info(1, "Player: Removed autogen track element from DOM");
    }
  });
}

function clickIsInsideMediaControls(event: MouseEvent): boolean {
  const controlsEl = document.getElementById("media-controls");
  if (!controlsEl) return false;
  const rect = controlsEl.getBoundingClientRect();
  return (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  );
}

function handleFixedWrapperClick(event: MouseEvent) {
  // Guard against iOS hit-test anomalies where control taps bubble/misroute to wrapper.
  if (playerStore.playerState.controlsVisible && clickIsInsideMediaControls(event)) {
    return;
  }
  playerStore.togglePlayer("fixedWrapper");
}
</script>

<template>
  <div id="player-wrapper" class="relative bg-black no-scrollbar">
    <div v-if="!playerStore.ready && !playerStore.error" class="absolute bg-black w-full h-full text-white flex items-center justify-center no-scrollbar"> 
      <div class="w-full h-full flex flex-col items-center justify-center">
        <Spinner class="w-20 h-20 max-w-[33%] max-h-[33%] !opacity-30"/>
        <div v-if="videoStore.video.source && videoStore.video.source.type_of == 'drive'" class="mt-4"> 
          {{ t('common.importing_from_google_drive') }}
        </div>
      </div>
    </div>
    <media-player
      ref="$player"
      :load="loadStrategy"
      :duration="providedDuration"
      id="media-player"
      class="@container/player w-full aspect-video bg-black text-white overflow-hidden ring-media-focus data-[focus]:ring-4 opacity-0 transition-opacity duration-500 no-scrollbar"
      :class="{
        'opacity-100': playerStore.ready || playerStore.error,
        'bg-white': videoStore.video?.design?.video_background_color == 'white',
        'cover-mode': videoStore.video?.design?.cover_mode,
      }"
      keyDisabled
      keyTarget="player"
      tabIndex="-1"
      :title="videoStore.video?.title"
      :src="$src"
      :crossOrigin="getCrossOrigin"
      disablePictureInPicture
      playsInline
      debug
      @seek="onSeek"
      @seeked="onSeeked"
      @error="onError"
      @play="onPlay"
      @pause="onPause"
      @ended="onEnd"
      @provider-change="onProviderChange"
      @auto-play-fail="onAutoPlayFail"
      @can-play="onCanPlay"
      @loaded-metadata="onLoadedMetadata"
      @load-start="onLoadStart"
      @time-update="onTimeUpdate"
      @waiting="onWaiting"
    >

      <media-provider id="media-provider" controlsDelay="10">
        <!-- <media-poster
          v-if="videoStore.video.design.thumbnail && !playerStore.started"
          :style="playerStore.layerStyle"
          class="absolute bg-black inset-0 block h-full w-full brightness-[70%] rounded-md opacity-0 transition-opacity data-[visible]:opacity-100 [&>img]:h-full [&>img]:w-[100%] [&>img]:object-cover"
          :src="cdnImageUrl(videoStore.video.design.thumbnail)"
          :alt="t('common.thumbnail')"
          crossOrigin="anonymous"
        /> -->
      </media-provider>
      <div id="fixed-wrapper-layer" class="absolute top-0 left-0 w-full h-full overflow-hidden no-scrollbar" :class="{ 'border-2 border-yellow-500': playerStore.isDebug }" @mousemove="doMouseMove" @click.stop="handleFixedWrapperClick">   
        <MasterLayer
          class="absolute inset-0 w-full h-full pointer-events-auto @container/player no-scrollbar"
          :style="masterLayerStyle"
          :autoplay="props.autoplay"
          :isMenu="props.isMenu"
          :isEnd="props.isEnd"
          :isNormal="isNormal"
          :quick-start-highlight-target="props.quickStartHighlightTarget"
        />
      </div>
      
      <VideoLayout
        v-if="showVideoLayout"
        :thumbnails="getThumbnails"
        :chapters="videoStore.video.chapters.length > 0"
        :useChapterTiming="videoStore.video.use_chapter_timing"
      />
      <ContainerDebug v-if="playerStore.isDebug" />
    </media-player>
    <!-- Add iframe debugger when in debug mode or via query param -->
    <IframeDebugger v-if="playerStore.isDebug || $route.query.iframe_debug === '1'" />
  </div>
</template>

<style>

#media-player {
  height: 100%;
  width: 100%;
}
#media-player video {
  height: 100%;
  width: 100%;
  object-fit: contain;
}
#media-player.cover-mode video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

html.ios-overflow-scroll-fix,
body.ios-overflow-scroll-fix {
  max-height: none !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
}

html.embed-wheel-pass-through,
body.embed-wheel-pass-through {
  overscroll-behavior: auto !important;
}
</style>
